// Web Push (этап 7.5): подписка устройства и её синхронизация с backend.
//
// iOS: push доступен только установленной на экран «Домой» PWA (standalone), а первое
// разрешение — только после явного жеста пользователя. Пароль/ключи здесь не хранятся:
// публичный VAPID-ключ приходит из /api/push/config.
//
// Этап 28: подписка держится сама (keepPushSubscription) — iOS удаляет её без спроса
// (правило WebKit о «тихих» push, см. sw-push.js), а выкатки и обновления тут ни при чём.
import { api } from './api.js';

// Выбор этого устройства: 'on' — push здесь включали (кнопкой, из чата или
// восстановлением), 'off' — выключили кнопкой «Отключить» или «Не нужно» на плашке.
// Нет значения — не решено (установки до 1.28.0). localStorage устройства: его не
// трогают ни выкатки, ни удаление подписки системой.
const PUSH_INTENT_KEY = 'petrovich-push-intent';

export function isPushSupported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export function isStandalone() {
  if (typeof window === 'undefined') {
    return false;
  }
  if (window.navigator.standalone === true) {
    return true;
  }
  return typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches;
}

export function isIos() {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return /iPhone|iPad|iPod/i.test(navigator.userAgent || '');
}

// applicationServerKey для PushManager.subscribe: base64url → Uint8Array.
export function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

async function getRegistration() {
  if (!isPushSupported()) {
    return null;
  }
  return navigator.serviceWorker.ready;
}

export async function getCurrentSubscription() {
  const registration = await getRegistration();
  if (!registration) {
    return null;
  }
  return registration.pushManager.getSubscription();
}

function subscriptionPayload(subscription) {
  const json = subscription.toJSON();
  return {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
    userAgent: typeof navigator !== 'undefined' ? String(navigator.userAgent || '').slice(0, 300) : null,
  };
}

// Включить push на этом устройстве: разрешение → подписка → сохранить на backend.
// Возвращает 'granted' | 'denied' | 'unsupported'.
export async function subscribeToPush(publicKey) {
  if (!isPushSupported() || !publicKey) {
    return 'unsupported';
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return 'denied';
  }
  const registration = await getRegistration();
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  await api.savePushSubscription(subscriptionPayload(subscription));
  setPushIntent('on');
  return 'granted';
}

export async function unsubscribeFromPush() {
  // Выбор человека: самовосстановление (keepPushSubscription) его больше не оспаривает.
  setPushIntent('off');
  const subscription = await getCurrentSubscription();
  if (!subscription) {
    return false;
  }
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe().catch(() => {});
  await api.deletePushSubscription(endpoint).catch(() => {});
  return true;
}

export function getPushIntent() {
  try {
    const value = window.localStorage.getItem(PUSH_INTENT_KEY);
    return value === 'on' || value === 'off' ? value : null;
  } catch (_) {
    return null;
  }
}

export function setPushIntent(value) {
  try {
    window.localStorage.setItem(PUSH_INTENT_KEY, value);
  } catch (_) {
    // Хранилище недоступно (приватный режим) — остаётся правило по 🔔.
  }
}

// Нужен ли push этому устройству: явный выбор здесь, иначе — есть ли у человека чаты
// с включёнными уведомлениями (🔔 хранит сервер, их не стирают ни iOS, ни выкатка).
export function isPushWanted(hasNotifiedChats) {
  const intent = getPushIntent();
  if (intent) {
    return intent === 'on';
  }
  return Boolean(hasNotifiedChats);
}

// Последний известный публичный VAPID-ключ: касанию «Включить» на плашке нельзя ждать
// сеть до запроса разрешения — iOS покажет запрос только внутри жеста.
let knownPublicKey = null;

export function getKnownPublicKey() {
  return knownPublicKey;
}

async function loadPublicKey() {
  const config = await api.getPushConfig();
  knownPublicKey = config?.enabled && config.publicKey ? config.publicKey : null;
  return knownPublicKey;
}

function sameServerKey(subscription, publicKey) {
  const current = subscription?.options?.applicationServerKey;
  if (!current) {
    return true; // браузер ключ не отдаёт — сравнить не с чем, подписку не трогаем
  }
  const have = new Uint8Array(current);
  const want = urlBase64ToUint8Array(publicKey);
  return have.length === want.length && have.every((byte, index) => byte === want[index]);
}

async function createSubscription(registration, publicKey) {
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });
  await api.savePushSubscription(subscriptionPayload(subscription));
  return subscription;
}

// Этап 28: держать подписку устройства живой без участия человека.
// resave (старт): подписка пересохраняется на backend — endpoint у push-сервиса может
// ротироваться, после перелогина её надо перепривязать — и сверяется с VAPID-ключом
// сервера. Без resave (возврат в приложение) — только проверка: пока подписка на месте,
// запросов нет.
// Итог: 'ok' — подписка есть; 'restored' — пересоздана без касаний; 'needs-tap' —
// разрешение сброшено, вернуть можно только касанием; 'off' — push здесь не нужен,
// не настроен на сервере или запрещён.
export async function keepPushSubscription({ wanted = false, resave = false } = {}) {
  if (!isPushSupported()) {
    return 'off';
  }
  const registration = await getRegistration();
  const subscription = await registration.pushManager.getSubscription();
  const permission = Notification.permission;

  if (subscription) {
    if (!resave) {
      return 'ok';
    }
    const publicKey = await loadPublicKey();
    if (publicKey && permission === 'granted' && !sameServerKey(subscription, publicKey)) {
      // Ключ сервера сменился: старую подписку push-сервис больше не примет.
      const oldEndpoint = subscription.endpoint;
      await subscription.unsubscribe().catch(() => {});
      await api.deletePushSubscription(oldEndpoint).catch(() => {});
      await createSubscription(registration, publicKey);
      return 'restored';
    }
    await api.savePushSubscription(subscriptionPayload(subscription));
    return 'ok';
  }

  if (!wanted || permission === 'denied') {
    return 'off';
  }
  const publicKey = await loadPublicKey();
  if (!publicKey) {
    return 'off';
  }
  if (permission !== 'granted') {
    return 'needs-tap';
  }
  // Разрешение на месте, подписку удалила система. При выданном разрешении WebKit
  // подписывает без жеста (PushManager.cpp проверяет жест только до разрешения).
  try {
    await createSubscription(registration, publicKey);
  } catch (error) {
    console.warn('push: silent resubscribe failed', error);
    return 'needs-tap';
  }
  setPushIntent('on');
  return 'restored';
}
